import { useNavigate } from 'react-router-dom'

interface PublicSideMenuProps {
  isOpen: boolean
  onClose: () => void
}

export default function PublicSideMenu({ isOpen, onClose }: PublicSideMenuProps) {
  const navigate = useNavigate()

  const menuItems = [
    {
      icon: 'features',
      label: 'Features',
      onClick: () => navigate('/features'),
      tooltip: 'See everything SSDL offers',
    },
    {
      icon: 'about',
      label: 'About',
      onClick: () => navigate('/about'),
      tooltip: 'Learn about SSDL and how it works',
    },
    {
      icon: 'heart',
      label: 'My Story',
      onClick: () => navigate('/my-story'),
      tooltip: 'Why SSDL exists and who built it',
    },
    {
      icon: 'security',
      label: 'Security',
      onClick: () => navigate('/security'),
      tooltip: 'How your data is protected',
    },
    {
      icon: 'key',
      label: 'Key Recovery',
      onClick: () => navigate('/recover'),
      tooltip: 'Recover your passcode via email',
    },
    {
      icon: 'help',
      label: 'Need Help?',
      onClick: () => navigate('/help'),
      tooltip: 'Crisis support and alcohol helplines',
      highlight: true,
    },
  ]

  return (
    <>
      {/* Backdrop */}
      <div
        className={`fixed inset-0 bg-black/50 z-50 transition-opacity duration-300 ${
          isOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'
        }`}
        onClick={onClose}
      />

      {/* Side Panel */}
      <div
        className={`fixed top-0 left-0 h-full w-72 bg-[var(--bg-secondary)] z-50 transform transition-transform duration-300 ease-out flex flex-col ${
          isOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        {/* Header */}
        <div className="flex items-center justify-between h-14 px-4 border-b border-[var(--border)]">
          <span className="text-[var(--text-primary)] font-bold">Menu</span>
          <button
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-[var(--bg-card)] transition-colors cursor-pointer"
            aria-label="Close menu"
          >
            <svg className="w-6 h-6 text-[var(--text-primary)]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Menu Items */}
        <div className="py-2 flex-1">
          {menuItems.map((item) => (
            <button
              key={item.label}
              onClick={() => {
                item.onClick()
                onClose()
              }}
              className="w-full flex items-center gap-3 px-4 py-3 hover:bg-[var(--bg-card)] transition-colors text-left cursor-pointer"
              title={item.tooltip}
            >
              <span className="w-8 h-8 rounded-lg bg-[var(--bg-card)] flex items-center justify-center">
                {item.icon === 'features' && (
                  <svg className="w-5 h-5 text-[var(--accent-teal)]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
                  </svg>
                )}
                {item.icon === 'about' && (
                  <svg className="w-5 h-5 text-[var(--accent-teal)]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                )}
                {item.icon === 'heart' && (
                  <svg className="w-5 h-5 text-[var(--accent-teal)]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 8.25c0-2.485-2.099-4.5-4.688-4.5-1.935 0-3.597 1.126-4.312 2.733-.715-1.607-2.377-2.733-4.313-2.733C5.1 3.75 3 5.765 3 8.25c0 7.22 9 12 9 12s9-4.78 9-12z" />
                  </svg>
                )}
                {item.icon === 'security' && (
                  <svg className="w-5 h-5 text-[var(--accent-teal)]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12.75L11.25 15 15 9.75m-3-7.036A11.959 11.959 0 013.598 6 11.99 11.99 0 003 9.749c0 5.592 3.824 10.29 9 11.623 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.571-.598-3.751h-.152c-3.196 0-6.1-1.248-8.25-3.285z" />
                  </svg>
                )}
                {item.icon === 'key' && (
                  <svg className="w-5 h-5 text-[var(--accent-teal)]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
                  </svg>
                )}
                {item.icon === 'help' && (
                  <svg className="w-5 h-5 text-[var(--bac-danger)]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 8.25c0-2.485-2.099-4.5-4.688-4.5-1.935 0-3.597 1.126-4.312 2.733-.715-1.607-2.377-2.733-4.313-2.733C5.1 3.75 3 5.765 3 8.25c0 7.22 9 12 9 12s9-4.78 9-12z" />
                  </svg>
                )}
              </span>
              <span className={item.highlight ? 'text-[var(--bac-danger)] font-semibold' : 'text-[var(--text-primary)]'}>{item.label}</span>
            </button>
          ))}
        </div>

        {/* Footer: Terms | Privacy */}
        <div className="border-t border-[var(--border)] px-4 py-3 flex items-center justify-center gap-3 text-sm text-[var(--text-secondary)]">
          <button
            onClick={() => { navigate('/terms'); onClose() }}
            className="hover:text-[var(--text-primary)] transition-colors cursor-pointer"
          >
            Terms
          </button>
          <span>|</span>
          <button
            onClick={() => { navigate('/privacy'); onClose() }}
            className="hover:text-[var(--text-primary)] transition-colors cursor-pointer"
          >
            Privacy
          </button>
        </div>
      </div>
    </>
  )
}
